"use client";

import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsivePopover } from "@/components/ui/responsive-popover";
import { Button } from "@/components/ui/button";
import { createEnhancedGenericForm } from "@/components/shared/EnhancedGenericCreateForm";
import { getEntityTableConfig } from "@/lib/entity-registry";
import type { Contacts } from "@/src/gql/graphql";

const ENHANCED_CONTACTS_CONFIG = getEntityTableConfig<Contacts>("contacts");
import { ClientInfoDisplay } from "./ClientInfoDisplay";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Alert } from "@/components/ui/alert";
import { Client, CreateContactForm as CreateContactFormType } from "@/lib/types/database";
import { ClientEditModal } from "./ClientDetails/ClientEditModal";
import { CompactDraftIndicator } from "@/components/ui/forms/draft-indicator";
import { ExternalLink, Mail, FileText, User, Phone } from "lucide-react";
import Link from "next/link";
// import { ComposeModalContainer } from "@/components/crm/messages/compose/ComposeModalContainer";
import { useTasks } from "@/lib/hooks/api/tasks";
import { useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { useReplyContext } from "@/components/crm/messages/contexts/ReplyContext";
import type { ComposeMessageModalData } from "@/lib/modals/data/modal-data-types";
import {
  fetchContactByEmail,
  createContact,
  updateContact,
} from "./contact-popover-api";
import { PopoverItemsSummary } from "@/components/ui/popover-items-summary";
import { useOpenContactMethodModals } from "@/lib/utils/crm/use-open-contact-method-modals";

interface ContactPopoverProps {
  name: string;
  email: string;
  children: React.ReactNode;
}

export const ContactPopover = ({ name, email, children }: ContactPopoverProps) => {
  const queryClient = useQueryClient();
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  // const [isComposeModalOpen, setIsComposeModalOpen] = useState(false);

  const {
    data: contact,
    isLoading,
    error,
  } = useQuery<Client | null, Error>({
    queryKey: ["contact", email],
    queryFn: () => fetchContactByEmail(email),
  });

  const { data: contactTasks = [] } = useTasks({
    contact_id: contact?.id,
    limit: 10,
  });

  const createMutation = useMutation({
    mutationFn: createContact,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact", email] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setCreateModalOpen(false);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateContactFormType) => {
      if (!contact) throw new Error("Contact not found");
      return updateContact(data, contact.id);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["contact", email] });
      void queryClient.invalidateQueries({ queryKey: ["contacts"] });
      setEditModalOpen(false);
    },
  });

  const { openModal } = useGlobalModalStack();
  const { setReplyingToMessage } = useReplyContext();
  const { openText } = useOpenContactMethodModals();

  const handleEmailClick = () => {
    if (!contact?.email) return;

    // Not replying to a specific message here – just composing to a contact
    setReplyingToMessage(null);

    // Pass data to the compose modal via modalData
    openModal(MODAL_IDS.messages.compose, {
      customerEmail: contact.email,
      customerName: contact.name,
    } as ComposeMessageModalData);
  };

  const handleCreateContact = (formData: CreateContactFormType) => {
    createMutation.mutate(formData);
  };

  const handleUpdateContact = async (formData: Partial<CreateContactFormType>) => {
    // Validate required fields
    if (!formData.name || !formData.email) {
      console.error("Missing required fields: name and email are required");
      return;
    }

    await updateMutation.mutateAsync(formData as CreateContactFormType);
  };

  const handleTextClick = () => {
    if (!contact?.phone) return;
    openText(String(contact.phone), {
      item: contact as unknown as Record<string, unknown>,
      entityType: "contacts",
    });
  };

  const popoverContent = (
    <div className="grid gap-4">
      <div className="space-y-2">
        <h4 className="font-medium leading-none">{name}</h4>
        <p className="text-sm text-muted-foreground">{email}</p>
      </div>
      <div className="grid gap-2">
        {isLoading && <LoadingSpinner />}
        {error && <Alert>Error: {error.message}</Alert>}
        {contact ? (
          <div className="space-y-3">
            <ClientInfoDisplay client={contact} sections={["contact"]} />

            <PopoverItemsSummary
              items={contactTasks}
              label="Tasks"
              viewAllHref={`/contacts/${contact.id}#tasks`}
            />

            <div className="grid grid-cols-2 gap-2">
              <Link href={`/contacts/${contact.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <ExternalLink className="h-3 w-3" />
                  View Details
                </Button>
              </Link>

              {contact.email && (
                <div className="flex items-center gap-1">
                  <Button
                    onClick={handleEmailClick}
                    variant="outline"
                    size="sm"
                    className="gap-2 flex-1"
                  >
                    <Mail className="h-3 w-3" />
                    Email
                  </Button>
                  <CompactDraftIndicator
                    type="compose"
                    identifier={contact.email}
                    onClick={handleEmailClick}
                  />
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Link href={`/contracts?client=${contact.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <FileText className="h-3 w-3" />
                  Contract
                </Button>
              </Link>

              {contact.phone && (
                <Button
                  onClick={handleTextClick}
                  variant="outline"
                  size="sm"
                  className="gap-2"
                >
                  <Phone className="h-3 w-3" />
                  Text / Call
                </Button>
              )}
            </div>

            <Button
              onClick={() => setEditModalOpen(true)}
              variant="ghost"
              size="sm"
              className="w-full gap-2"
            >
              <User className="h-3 w-3" />
              Edit Contact
            </Button>
          </div>
        ) : (
          <Button onClick={() => setCreateModalOpen(true)}>Create Contact</Button>
        )}
      </div>
      {isCreateModalOpen && (
        <div className="mt-4 pt-4 border-t">
          {React.createElement(
            createEnhancedGenericForm("contacts", ENHANCED_CONTACTS_CONFIG),
            {
              onSubmit: (data: Partial<Client>) =>
                handleCreateContact(data as unknown as CreateContactFormType),
              initialValues: { email, name },
            }
          )}
          <Button variant="ghost" onClick={() => setCreateModalOpen(false)} className="mt-4 w-full">
            Cancel
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <>
      <ResponsivePopover
        content={popoverContent}
        title="Contact Details"
        className="w-80"
        align="center"
        popoverSide="bottom"
        drawerSide="bottom"
      >
        {children}
      </ResponsivePopover>

      {contact && (
        <ClientEditModal
          client={contact}
          isOpen={isEditModalOpen}
          onClose={() => setEditModalOpen(false)}
          onSave={handleUpdateContact}
        />
      )}

      {/* Compose Email Modal */}
      {/* {contact && (
        <ComposeModalContainer
          isOpen={isComposeModalOpen}
          onOpenChange={setIsComposeModalOpen}
          originalMessage={undefined}
          initialRecipient={contact.email}
          initialClient={contact}
        />
      )} */}
    </>
  );
};
